/**
 * THE FROST PATCH — drop a sheet of ice behind you, and the first person who crosses it spends
 * ten seconds at half speed.
 *
 * The player report: "another power-up could be frost which you hold down and it lays a streak on
 * the ground (or even just a one time click and lays down a frost area) where the first person who
 * hits it can still move but their movement speed is cut in half for the next 10 seconds".
 *
 * **ONE CLICK, NOT A HELD STREAK, and that is a decision rather than a simplification.** The
 * report offers both and this takes the single drop. `usePowerUp` is an *edge* on the input frame
 * — one press is one firing, spent by the next tick, exactly as `split` and `eject` are — so
 * "hold to lay a trail" is not a tuning of this ability, it is a new *held* action shape on the
 * client -> server surface, plus a decision about what happens to a trail when the socket stalls
 * mid-stroke. The mine already proves the dropped-body shape works and a single patch is already
 * the fence the report is describing; a streak is one more `PowerUp` in this directory the day a
 * card asks for it.
 *
 * **IT IS THE FIRST ABILITY THAT IS AREA DENIAL WITHOUT BEING DAMAGE.** A rocket and a mine break
 * a cluster, a chain decides where somebody is, a ray takes their agency outright; this leaves the
 * body, the mass and the steering entirely alone and takes only the one thing an .io game is
 * actually about, which is how fast you close a gap. It is therefore the answer to a leader who
 * wins every chase by being unavoidable, and it is worth nothing at all against somebody who was
 * not going anywhere.
 *
 * **IT IS SPENT BY THE FIRST PLAYER WHO CROSSES IT** — "the first person who hits it", in the
 * report's own words, and the reading this file takes literally. That is also what bounds its
 * cost: a patch that slowed everybody who touched it for as long as it lay there would be a
 * standing tax on a piece of the map for thirty seconds, from one orb, for one press. So it
 * retires on contact exactly as a mine does, and the whole of the difference between the two is
 * what contact *leaves behind* — see `SlowProjectileSpec`.
 *
 * **IT IS VISIBLE TO EVERYONE**, for the mine's stated reason and it is worth repeating here
 * because this ability is the one where it matters most: a hidden slow field turns a chase into a
 * coin flip the chaser had no way to read, and there is no counter to it but to stop chasing
 * anybody. A visible one is a *fence* — it says "not this way", going round it is the time the
 * person who laid it bought, and driving through it anyway is a price somebody chose to pay.
 */

import { TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_FROST = 'frost';

/**
 * How long a player who crosses a patch is slowed for, in seconds. **Ten**, which is the number
 * the report asked for, and it is twice FREEZE_DURATION_SECONDS on purpose: this takes half of
 * something where the ray takes all of it, so the two abilities are roughly the same amount of
 * time removed from a run and differ entirely in whether you get to spend it.
 */
export const SLOW_DURATION_SECONDS = 10;

/** The same number the simulation counts in. `PlayerState.slowedUntilTick` is a tick. */
export const SLOW_DURATION_TICKS = SLOW_DURATION_SECONDS * TICK_RATE_HZ;

/**
 * What a slowed player's steering is multiplied by — **a half**, the report's own figure.
 *
 * It is a constant here rather than a field on the spec, and that is deliberate: the status is on
 * the *player* and outlives the body that delivered it, so at the tick `stepMovement` reads it
 * the projectile is long gone and there is no spec to read a multiplier off. It is the freeze's
 * arrangement exactly — `freezeTicks` is on the spec because it decides how long the deadline
 * runs, and "a frozen player does not move" is not on the spec at all, because it is what the
 * status *means*.
 *
 * A half is a whole mechanic rather than a nudge. Growing from a starting blob all the way to the
 * largest one the game allows costs a player BASE_SPEED - MIN_SPEED and nothing more — that is the
 * whole of what `massToSpeed` can do to anybody — and this takes half of whatever they have,
 * instantly and on top of it. It is the same order of change as the entire mass curve.
 */
export const SLOW_SPEED_MULTIPLIER = 0.5;

/**
 * Ticks before the patch is live — one second, the mine's, for the mine's reason.
 *
 * It is laid a body's length behind a moving player and a player who is turning can be back on
 * top of it inside a few ticks, so this is the window that separates "laid it behind me while
 * running" from "reversed onto my own ice". It matters here for the same reason it matters there
 * and no less: this patch catches its owner too (`hitsOwner`).
 */
export const FROST_ARM_TICKS = TICK_RATE_HZ;

/**
 * How long it lies there, in ticks — thirty seconds, the mine's again.
 *
 * A patch that never expired would make the map slowly fill with ice nobody remembers laying, and
 * the arena thirty seconds from now should be the arena you can see.
 */
export const FROST_LIFETIME_TICKS = 30 * TICK_RATE_HZ;

/**
 * Wider than a mine and narrower than a spike, because it is an *area* rather than a point.
 *
 * A mine is a thing you steer around by a body's width; this is meant to read as a piece of the
 * floor that is not worth crossing, and something you can clip the edge of by accident while
 * chasing somebody who went straight through the middle of it.
 */
export const FROST_RADIUS = 50;

/** How far behind the laying cell's rim it settles — the mine's drop, for the mine's reason. */
export const FROST_DROP_DISTANCE = 40;

/**
 * Firings one orb is worth. **Five**, and it is the player's own number: "the frost should maybe
 * be 5 uses."
 *
 * Charged rather than timed for the freeze's reason and one of its own: a patch is *area denial*,
 * so what a player is buying is the choice of five pieces of the map, and a clock on it would make
 * that choice "wherever I happened to be standing when the timer ran out". Five patches is also as
 * many as the mechanic can hold up at once — FROST_LIFETIME_TICKS is thirty seconds and this
 * cooldown is one, so a player who lays all five keeps all five on the field.
 */
export const FROST_CHARGES = 5;

/**
 * The gap between two patches, in ticks — **FROST_ARM_TICKS, one second.**
 *
 * Derived: a patch is scenery until it arms and a fence afterwards, so the next one may be laid
 * on the tick the last one becomes a fence. Short, because unlike a ray a patch does nothing to
 * anybody by itself — it has to be driven into — and a trail of five laid in five seconds is the
 * fence the ability is for rather than five effects landing on five people.
 */
export const FROST_FIRE_COOLDOWN_TICKS = FROST_ARM_TICKS;

export const frostPowerUp: PowerUp = {
  id: POWER_UP_FROST,
  name: 'Frost patch',
  description:
    'Lays ice behind you; the first player across it moves at half speed for ten seconds.',
  glyph: '🧊',
  use: {
    model: 'charged',
    charges: FROST_CHARGES,
    cooldownTicks: FROST_FIRE_COOLDOWN_TICKS,
  },
  projectile: {
    motion: 'dropped',
    effect: 'slow',
    speed: 0,
    range: 0,
    lifetimeTicks: FROST_LIFETIME_TICKS,
    armingTicks: FROST_ARM_TICKS,
    radius: FROST_RADIUS,
    standoff: FROST_DROP_DISTANCE,
    // True, and for the mine's reason rather than by inheritance: a hazard you can reverse over
    // in safety is a hazard with no downside, and the arming delay above is exactly the window
    // that makes laying one while fleeing free.
    hitsOwner: true,
    slowTicks: SLOW_DURATION_TICKS,
  },
};
